import React, { useState, useMemo, useEffect } from "react";
import {
  useGetEmailDeliveryLogsQuery,
  useGetEmailDeliveryLogStatsQuery,
  useGetEmailDeliveryLogByIdQuery,
} from "apis/emailDeliveryLog";
import PageLoader from "components/ui/PageLoader";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import Table from "components/ui/Table";
import SearchSelect from "components/ui/SearchSelect";
import { formatDateTime } from "utils/helperFunctions";

const PROVIDERS = [
  { value: "", label: "All Providers" },
  { value: "sendgrid", label: "SendGrid" },
  { value: "google", label: "Gmail" },
];

// One control for what is two backend params. `outcome` is the coarse grouping
// (did it land?), `status` is the exact state — offering both as separate
// dropdowns would let you select a contradictory pair.
const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "outcome:delivered", label: "Delivered (all recipients)" },
  { value: "outcome:problem", label: "Problems (any)" },
  { value: "outcome:partial", label: "— Partial (some failed)" },
  { value: "outcome:pending", label: "No delivery event yet" },
  { value: "status:bounced", label: "— Bounced" },
  { value: "status:blocked", label: "— Blocked" },
  { value: "status:dropped", label: "— Dropped" },
  { value: "status:spamreport", label: "— Spam report" },
  { value: "status:deferred", label: "— Deferred" },
  { value: "status:failed", label: "— Send failed" },
  { value: "status:sent", label: "— Sent" },
];

const ENGAGEMENT_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

const TIME_FRAMES = [
  { value: "", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "lastWeek", label: "Last 7 Days" },
  { value: "lastMonth", label: "Last 30 Days" },
  { value: "last3Months", label: "Last 3 Months" },
  { value: "last6Months", label: "Last 6 Months" },
  { value: "custom", label: "Custom Range" },
];

const STATUS_CLASSES = {
  delivered: "bg-green-100 text-green-800",
  sent: "bg-blue-100 text-blue-800",
  queued: "bg-gray-100 text-gray-800",
  deferred: "bg-yellow-100 text-yellow-800",
  bounced: "bg-red-100 text-red-800",
  blocked: "bg-red-100 text-red-800",
  dropped: "bg-red-100 text-red-800",
  spamreport: "bg-orange-100 text-orange-800",
  failed: "bg-red-200 text-red-900",
  // Neither success nor failure — some recipients got it, some did not.
  partial: "bg-amber-100 text-amber-900",
};

const RECIPIENT_STATUS_OPTIONS = [
  { value: "", label: "Any" },
  { value: "problem", label: "Any recipient failed" },
  { value: "bounced", label: "Bounced" },
  { value: "blocked", label: "Blocked" },
  { value: "dropped", label: "Dropped" },
  { value: "spamreport", label: "Spam report" },
  { value: "delivered", label: "Delivered" },
  { value: "deferred", label: "Deferred" },
];

const initialFilters = {
  recipientStatus: "",
  provider: "",
  statusChoice: "",
  type: "",
  recipient: "",
  fromEmail: "",
  opened: "",
  clicked: "",
  search: "",
  timeFrame: "",
  startDate: "",
  endDate: "",
};

const PAGE_SIZE = 25;

const getOption = (options, value) =>
  options.find((o) => o.value === value) || null;

const formatRate = (rate) =>
  rate === null || rate === undefined ? "-" : `${(rate * 100).toFixed(1)}%`;

// Recipient lists get long. Show the first, and how many more there are.
const summarizeRecipients = (log) => {
  const to = log?.toEmails || [];
  const extra = (log?.recipients || []).length - to.length;
  if (!to.length) return "-";
  const head = to[0];
  const rest = to.length - 1 + Math.max(extra, 0);
  return rest > 0 ? `${head} +${rest}` : head;
};

const EmailDeliveryLogs = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);

  const queryParams = useMemo(() => {
    const params = { page: currentPage, limit: PAGE_SIZE };
    Object.entries(appliedFilters).forEach(([key, value]) => {
      if (value === "" || value === null || value === undefined) return;
      if (key === "timeFrame" && value === "custom") return;

      // statusChoice carries which param it means in its own value.
      if (key === "statusChoice") {
        const [param, choice] = value.split(":");
        params[param] = choice;
        return;
      }
      params[key] = value;
    });
    return params;
  }, [appliedFilters, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilters]);

  const {
    data: logs,
    pagination,
    isPending,
    isFetching,
  } = useGetEmailDeliveryLogsQuery(queryParams);
  const { data: stats } = useGetEmailDeliveryLogStatsQuery(queryParams);

  const isCustomRange = filters.timeFrame === "custom";

  const typeOptions = useMemo(
    () => [
      { value: "", label: "All Types" },
      ...(stats?.availableTypes || []).map((t) => ({ value: t, label: t })),
    ],
    [stats?.availableTypes],
  );

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => setAppliedFilters(filters);

  const clearFilters = () => {
    setFilters(initialFilters);
    setAppliedFilters(initialFilters);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "createdAt",
        header: "Sent",
        cell: (row) => (
          <span className="text-xs whitespace-nowrap">
            {formatDateTime(row.createdAt) ?? "-"}
          </span>
        ),
        isFilter: false,
        minWidth: "170px",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (row) => (
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              STATUS_CLASSES[row.status] || "bg-gray-100 text-gray-800"
            }`}
          >
            {row.status}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "provider",
        header: "Via",
        cell: (row) => (
          <span className="text-xs">
            {row.provider === "google" ? "Gmail" : "SendGrid"}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: (row) => (
          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-800">
            {row.type || "-"}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "toEmails",
        header: "To",
        cell: (row) => (
          <span className="text-xs break-words">{summarizeRecipients(row)}</span>
        ),
        isFilter: false,
        maxWidth: "220px",
      },
      {
        accessorKey: "deliveredCount",
        header: "Delivered",
        cell: (row) => {
          // Gmail reports nothing, so a 0/2 there means "unknown", not "failed".
          if (row.provider === "google") {
            return <span className="text-xs text-darkgray">n/a</span>;
          }
          const total = row.recipientCount || 0;
          if (!total) return <span className="text-xs text-darkgray">—</span>;
          const done = row.deliveredCount || 0;
          const bad = row.problemCount || 0;
          return (
            <span
              className={`text-xs whitespace-nowrap ${
                bad ? "text-red-700 font-medium" : "text-darkergray"
              }`}
            >
              {done}/{total}
              {bad ? ` · ${bad} failed` : ""}
            </span>
          );
        },
        isFilter: false,
      },
      {
        accessorKey: "subject",
        header: "Subject",
        cell: (row) => (
          <span className="break-words">{row.subject || "-"}</span>
        ),
        isFilter: false,
        maxWidth: "280px",
      },
      {
        accessorKey: "fromEmail",
        header: "From",
        cell: (row) => <span className="text-xs">{row.fromEmail || "-"}</span>,
        isFilter: false,
        maxWidth: "200px",
      },
      {
        accessorKey: "openCount",
        header: "Opens",
        cell: (row) =>
          row.provider === "google" ? (
            <span className="text-xs text-darkgray">n/a</span>
          ) : (
            <span className="text-xs">{row.openCount || 0}</span>
          ),
        isFilter: false,
      },
      {
        accessorKey: "clickCount",
        header: "Clicks",
        cell: (row) =>
          row.provider === "google" ? (
            <span className="text-xs text-darkgray">n/a</span>
          ) : (
            <span className="text-xs">{row.clickCount || 0}</span>
          ),
        isFilter: false,
      },
      {
        accessorKey: "bounceReason",
        header: "Reason",
        cell: (row) => (
          <span className="text-xs text-red-700 break-words">
            {row.bounceReason || row.sendError || "-"}
          </span>
        ),
        isFilter: false,
        maxWidth: "260px",
      },
    ],
    [],
  );

  if (isPending) return <PageLoader />;

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold text-darkergray mb-6">
        Email Delivery Logs
      </h1>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
          <StatCard label="Total" value={stats.totals?.total || 0} />
          <StatCard
            label="Delivered"
            value={stats.totals?.delivered || 0}
            color="text-green-700"
          />
          <StatCard
            label="Problems"
            value={stats.totals?.problems || 0}
            color="text-red-700"
          />
          <StatCard label="Opened" value={stats.totals?.opened || 0} />
          <StatCard label="Clicked" value={stats.totals?.clicked || 0} />
          <StatCard
            label="Open Rate"
            value={formatRate(stats.totals?.openRate)}
          />
          <StatCard
            label="SendGrid"
            value={stats.providerCounts?.sendgrid || 0}
          />
          <StatCard label="Gmail" value={stats.providerCounts?.google || 0} />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-mediumgray rounded-lg p-4 mb-6">
        <h2 className="text-lg font-semibold text-darkgray mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <FilterField label="Status">
            <SearchSelect
              options={STATUS_OPTIONS}
              value={getOption(STATUS_OPTIONS, filters.statusChoice)}
              onChange={(opt) =>
                handleFilterChange("statusChoice", opt?.value || "")
              }
              placeholder="All Statuses"
              searchable={false}
            />
          </FilterField>

          <FilterField label="Provider">
            <SearchSelect
              options={PROVIDERS}
              value={getOption(PROVIDERS, filters.provider)}
              onChange={(opt) =>
                handleFilterChange("provider", opt?.value || "")
              }
              placeholder="All Providers"
              searchable={false}
            />
          </FilterField>

          <FilterField label="Email Type">
            <SearchSelect
              options={typeOptions}
              value={getOption(typeOptions, filters.type)}
              onChange={(opt) => handleFilterChange("type", opt?.value || "")}
              placeholder="All Types"
            />
          </FilterField>

          <FilterField label="Recipient">
            <input
              className="form-input border border-mediumgray w-full"
              type="text"
              placeholder="Exact address, e.g. jane@lawfirm.com.au"
              value={filters.recipient}
              onChange={(e) => handleFilterChange("recipient", e.target.value)}
            />
          </FilterField>

          <FilterField label="Sender">
            <input
              className="form-input border border-mediumgray w-full"
              type="text"
              placeholder="e.g. sandy@"
              value={filters.fromEmail}
              onChange={(e) => handleFilterChange("fromEmail", e.target.value)}
            />
          </FilterField>

          <FilterField label="Search">
            <input
              className="form-input border border-mediumgray w-full"
              type="text"
              placeholder="Subject, body preview, bounce reason…"
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
            />
          </FilterField>

          <FilterField label="Any Recipient">
            <SearchSelect
              options={RECIPIENT_STATUS_OPTIONS}
              value={getOption(RECIPIENT_STATUS_OPTIONS, filters.recipientStatus)}
              onChange={(opt) =>
                handleFilterChange("recipientStatus", opt?.value || "")
              }
              placeholder="Any"
              searchable={false}
            />
          </FilterField>

          <FilterField label="Opened">
            <SearchSelect
              options={ENGAGEMENT_OPTIONS}
              value={getOption(ENGAGEMENT_OPTIONS, filters.opened)}
              onChange={(opt) => handleFilterChange("opened", opt?.value || "")}
              placeholder="All"
              searchable={false}
            />
          </FilterField>

          <FilterField label="Clicked">
            <SearchSelect
              options={ENGAGEMENT_OPTIONS}
              value={getOption(ENGAGEMENT_OPTIONS, filters.clicked)}
              onChange={(opt) => handleFilterChange("clicked", opt?.value || "")}
              placeholder="All"
              searchable={false}
            />
          </FilterField>

          <FilterField label="Date Range">
            <SearchSelect
              options={TIME_FRAMES}
              value={getOption(TIME_FRAMES, filters.timeFrame)}
              onChange={(opt) =>
                handleFilterChange("timeFrame", opt?.value || "")
              }
              placeholder="All Time"
              searchable={false}
            />
          </FilterField>

          {isCustomRange && (
            <>
              <FilterField label="Start Date">
                <input
                  className="form-input border border-mediumgray w-full"
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value)
                  }
                />
              </FilterField>
              <FilterField label="End Date">
                <input
                  className="form-input border border-mediumgray w-full"
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange("endDate", e.target.value)}
                />
              </FilterField>
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <Button className="gray-button" onClick={clearFilters}>
            Clear
          </Button>
          <Button className="black-button" onClick={applyFilters}>
            Apply Filters
          </Button>
        </div>
      </div>

      <Table
        title={isFetching ? "Email Logs (loading…)" : "Email Logs"}
        columns={columns}
        data={logs || []}
        pageSize={PAGE_SIZE}
        searchable={false}
        serverPagination={
          pagination
            ? {
                page: pagination.page,
                totalPages: pagination.totalPages,
                total: pagination.total,
                limit: pagination.limit,
              }
            : null
        }
        onPageChange={setCurrentPage}
        onRowClick={(row) => setSelectedId(row._id)}
        emptyText="No emails found."
      />

      <LogDetailModal
        id={selectedId}
        isOpen={!!selectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
};

const FilterField = ({ label, children }) => (
  <div>
    <label className="block text-xs font-medium text-darkergray mb-1">
      {label}
    </label>
    {children}
  </div>
);

const StatCard = ({ label, value, color = "text-darkergray" }) => (
  <div className="bg-white border border-mediumgray rounded-lg p-3 text-center">
    <div className={`text-xl font-bold ${color}`}>{value}</div>
    <div className="text-xs text-darkgray uppercase tracking-wide">{label}</div>
  </div>
);

// The list query drops the events array to keep pages small, so the detail view
// refetches the single row to get its delivery history.
const LogDetailModal = ({ id, isOpen, onClose }) => {
  const { data: log, isPending } = useGetEmailDeliveryLogByIdQuery(id);

  if (!isOpen) return null;

  const events = Array.isArray(log?.events) ? [...log.events].reverse() : [];
  const isGmail = log?.provider === "google";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Email Detail" className="max-w-4xl">
      {isPending || !log ? (
        <div className="p-8 text-center text-darkgray text-sm">Loading…</div>
      ) : (
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <DetailRow label="Sent" value={formatDateTime(log.createdAt) ?? "-"} />
            <DetailRow label="Status" value={log.status} />
            <DetailRow label="Provider" value={isGmail ? "Gmail" : "SendGrid"} />
            <DetailRow label="Type" value={log.type || "-"} />
            <DetailRow label="From" value={log.fromEmail} />
            <DetailRow label="From Name" value={log.fromName || "-"} />
            <DetailRow label="Reply-To" value={log.replyTo || "-"} />
            <DetailRow
              label="Attachments"
              value={log.attachmentCount || 0}
            />
            <DetailRow
              label="Delivered"
              value={
                formatDateTime(log.deliveredAt) ?? "-"
              }
            />
            <DetailRow
              label="First Opened"
              value={
                formatDateTime(log.firstOpenedAt) ?? "-"
              }
            />
            <DetailRow
              label="Opens / Clicks"
              value={
                isGmail
                  ? "Not tracked for Gmail"
                  : `${log.openCount || 0} / ${log.clickCount || 0}`
              }
            />
            <DetailRow
              label="Delivered"
              value={
                isGmail
                  ? "Not reported for Gmail"
                  : `${log.deliveredCount || 0} of ${log.recipientCount || 0}`
              }
            />
            <DetailRow
              label="Property"
              value={log.propertyId?.address || log.propertyId?._id || "-"}
            />
            <DetailRow label="Message ID" value={log.messageId} />
            <DetailRow
              label="Provider Message ID"
              value={log.providerMessageId || "-"}
            />
          </div>

          <DetailBlock label="Subject" value={log.subject || "-"} />

          <RecipientOutcomes log={log} />

          {log.bounceReason && (
            <DetailBlock
              label={`Failure Reason${log.bounceType ? ` (${log.bounceType})` : ""}`}
              value={log.bounceReason}
              valueClass="text-red-700"
            />
          )}

          {log.sendError && (
            <DetailBlock
              label="Send Error"
              value={log.sendError}
              valueClass="text-red-700"
            />
          )}

          {log.preview && (
            <DetailBlock label="Body Preview" value={log.preview} />
          )}

          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-2">
              Delivery Events ({events.length})
            </div>
            {events.length === 0 ? (
              <div className="border border-dashed border-mediumgray rounded p-4 text-center text-xs text-darkgray">
                {isGmail
                  ? "Gmail does not report delivery events. The row stops at “sent”."
                  : "No events received yet."}
              </div>
            ) : (
              <ul className="space-y-2 max-h-80 overflow-y-auto">
                {events.map((event, index) => (
                  <li
                    key={`${event.event}-${event.timestamp}-${index}`}
                    className="bg-lightgray border border-mediumgray rounded p-3"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          STATUS_CLASSES[event.event] ||
                          "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {event.event}
                      </span>
                      <span className="text-xs text-darkgray whitespace-nowrap">
                        {formatDateTime(event.timestamp) ?? "-"}
                      </span>
                    </div>
                    <div className="text-xs text-darkergray mt-1 break-words">
                      {event.email}
                      {event.reason ? ` — ${event.reason}` : ""}
                      {event.url ? ` — ${event.url}` : ""}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

// The point of the per-recipient model: which address got it, which did not,
// and why. `field` maps each address back to the envelope header it came from,
// since recipientStatus is a flat list.
const RecipientOutcomes = ({ log }) => {
  const fieldOf = (email) => {
    const has = (list) =>
      (list || []).some((e) => String(e).toLowerCase() === email);
    if (has(log.toEmails)) return "To";
    if (has(log.ccEmails)) return "Cc";
    if (has(log.bccEmails)) return "Bcc";
    return "";
  };

  const rows = log.recipientStatus || [];

  if (!rows.length) {
    // Failed sends have no per-recipient state — nothing reached anyone — so
    // fall back to who it was addressed to.
    const all = [
      ...(log.toEmails || []).map((e) => ["To", e]),
      ...(log.ccEmails || []).map((e) => ["Cc", e]),
      ...(log.bccEmails || []).map((e) => ["Bcc", e]),
    ];
    return (
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-2">
          Addressed To ({all.length}) — never sent
        </div>
        <ul className="text-xs text-darkergray space-y-1">
          {all.map(([field, email]) => (
            <li key={`${field}-${email}`}>
              <span className="text-darkgray mr-2">{field}</span>
              {email}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-2">
        Recipients ({rows.length})
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <tbody>
            {rows.map((r) => (
              <tr key={r.email} className="border-b border-mediumgray">
                <td className="py-2 pr-2 text-darkgray whitespace-nowrap align-top">
                  {fieldOf(String(r.email).toLowerCase())}
                </td>
                <td className="py-2 pr-2 break-all align-top">{r.email}</td>
                <td className="py-2 pr-2 align-top">
                  <span
                    className={`px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                      STATUS_CLASSES[r.status] || "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {r.status}
                  </span>
                </td>
                <td className="py-2 pr-2 text-darkgray whitespace-nowrap align-top">
                  {r.openCount || 0}o / {r.clickCount || 0}c
                </td>
                <td className="py-2 text-red-700 break-words align-top">
                  {r.reason || ""}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-xs uppercase tracking-wide text-darkgray">
      {label}
    </span>
    <span className="text-darkergray break-words">{String(value ?? "-")}</span>
  </div>
);

const DetailBlock = ({ label, value, valueClass = "" }) => (
  <div>
    <div className="text-xs font-semibold uppercase tracking-wide text-darkgray mb-1">
      {label}
    </div>
    <div
      className={`bg-lightgray border border-mediumgray rounded p-3 text-sm whitespace-pre-wrap break-words ${valueClass}`}
    >
      {value}
    </div>
  </div>
);

export default EmailDeliveryLogs;
